
import { api } from './base';
import type { RequestOptions } from './base';

export interface WikiDocumentSummary {
  id: string;
  urlId: string;
  title: string;
  icon: string | null;
  collectionId: string | null;
  parentDocumentId: string | null;
  updatedAt: string | null;
}

export interface WikiSearchHit extends WikiDocumentSummary {
  snippet: string | null;
}

export interface WikiCatalogueItem extends WikiSearchHit {
  description: string;
  customDescription: string | null;
  coverUrl: string | null;
  coverPositionX: number;
  coverPositionY: number;
}

export interface WikiPresentationInput {
  description: string;
  coverPositionX: number;
  coverPositionY: number;
  removeCover: boolean;
  file?: File | null;
}

export interface WikiCollectionTree {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  documents: WikiDocumentSummary[];
}

export interface WikiDocumentDetail extends WikiDocumentSummary {
  text: string;
  collection: { id: string; name: string; icon: string | null } | null;
  breadcrumbs: { urlId: string; title: string }[];
  children: WikiDocumentSummary[];
  outlineUrl: string | null;
}

export interface WikiCollectionAdminRow {
  id: string;
  name: string;
  icon: string | null;
  published: boolean;
  documents: number;
  syncedAt: string | null;
}

export interface WikiSyncStatus {
  lastFullSyncAt: string | null;
  lastFullSyncMs: number | null;
  lastWebhookAt: string | null;
  documents: number;
  collections: number;
  lastError: string | null;
  collectionsInOutline: number | null;
  collectionsBlockedByEnv: number | null;
  envCeilingEnabled: boolean;
}

export const wikiApi = {
  catalogue: (
    params: { query?: string; limit?: number; offset?: number },
    opts?: RequestOptions,
  ) => api.get<WikiCatalogueItem[]>('/api/v1/wiki/catalogue', params, opts),
  savePresentation: (id: string, input: WikiPresentationInput) => {
    const form = new FormData();
    form.append('description', input.description);
    form.append('coverPositionX', String(input.coverPositionX));
    form.append('coverPositionY', String(input.coverPositionY));
    form.append('removeCover', String(input.removeCover));
    if (input.file) form.append('file', input.file);
    return api.postForm<{ saved: boolean }>(`/api/v1/admin/wiki/documents/${id}/presentation`, form, { toast: false });
  },
  collections: () => api.get<WikiCollectionTree[]>('/api/v1/wiki/collections'),
  documents: (
    params: { query?: string; collectionId?: string; limit?: number; offset?: number },
    opts?: RequestOptions,
  ) => api.get<WikiSearchHit[]>('/api/v1/wiki/documents', params, opts),
  suggest: (query: string) =>
    api.get<WikiDocumentSummary[]>('/api/v1/wiki/documents/suggest', { query }),
  document: (urlId: string, opts?: RequestOptions) =>
    api.get<WikiDocumentDetail>(`/api/v1/wiki/documents/${urlId}`, undefined, opts),
  syncStatus: () => api.get<WikiSyncStatus>('/api/v1/admin/wiki/sync/status'),
  sync: () =>
    api.post<{ enqueued: boolean; alreadyRunning: boolean }>('/api/v1/admin/wiki/sync', {}),
  adminCollections: () => api.get<WikiCollectionAdminRow[]>('/api/v1/admin/wiki/collections'),
  setPublished: (id: string, published: boolean) =>
    api.patch<{ id: string; published: boolean }>(`/api/v1/admin/wiki/collections/${id}`, {
      published,
    }),
};
